import Config from '@config';
import config from '@config';
import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import { useCallback, useEffect, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import SlideInSection from '@molecules/SlideInSection/SlideInSection';
import StoreSearch from '@molecules/StoreSearch/StoreSearch';
import pushGTMEvent from '@helpers/analyticsHelpers/pushGTMEvent';
import { getStoreInformation } from '@api/interfaces/storeApi';
import { AxfoodStoreInfoViewModel } from '@occ/api-client';
import Link from '@atoms/Link/Link';
import useStoreAndLocaleState from '@molecules/PromotionTabs/useStoreAndLocaleState';
import paths from '@constants/paths';
import {
  StyledButton,
  StyledOfflineTextWrapper,
  StyledPromotionTabsWrapper,
  StyledStoreSearchTitle,
} from './PromotionTabs.styles';
import useSnackBar from '@hooks/useSnackbar';
import Store from '@icons/store.svg';
import Cart from '@icons/cart.svg';
import DeliveryBox from '@molecules/DeliveryBox/DeliveryBox';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useLocalStorage from '@hooks/useLocalStorage';
import { toggleSlideInSection } from '@slices/slideInSectionSlice';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { openDeliveryPicker } from '@slices/deliveryPickerSlice';
import { trackOfflineStoreSet } from '@helpers/analyticsHelpers/trackStore';
import useFeatureToggle from '@hooks/useFeatureToggle';
import useStoreFlyerData from '@hooks/useStoreFlyerData';
import StoreFlyerModal from './StoreFlyerModal/StoreFlyerModal';

interface Props {
  activeTab?: string | null;
  ecommerceSelected: boolean;
  setSelectedOfflineStore: (s: AxfoodStoreInfoViewModel) => void;
  selectedOfflineStore: AxfoodStoreInfoViewModel;
  shouldOpenFlyer?: boolean;
}

const PromotionTabs = ({
  activeTab,
  ecommerceSelected,
  setSelectedOfflineStore,
  selectedOfflineStore,
  shouldOpenFlyer,
}: Props) => {
  const router = useRouter();
  const { store } = useStore();
  const { cart } = useCart();
  const { setSnack } = useSnackBar();
  const dispatch = useAppDispatch();
  const t = useTranslations('promotionPage');
  const [, setPromotionStoreId] = useLocalStorage(config.LOCAL_STORAGE_SELECTORS.promotionStoreId, '');

  const [isSlideInOpen, setIsSlideInOpen] = useState(false);
  const [isNavigating, startNavigation] = useTransition();
  const DELIVERY_COPY = { [Config.HOME_DELIVERY]: t('deliverTo'), [Config.PICKUP_IN_STORE]: t('pickUpFrom') };
  const deliveryMode = cart?.deliveryModeCode || '';
  const deliveryCopy = DELIVERY_COPY[deliveryMode];
  const showDeliveryInformation =
    store?.activelySelected &&
    ((deliveryMode === Config.PICKUP_IN_STORE && store.name) ||
      (deliveryMode === Config.HOME_DELIVERY && cart?.deliveryAddress?.formattedAddress));

  const isStoreTab = activeTab === Config.ROUTES.promotionpage.store;
  const isEcommerceTab = activeTab === Config.ROUTES.promotionpage.ecommerce;
  const { storeId, flyerURL, name } = selectedOfflineStore || {};

  const { isEnabled: isNextWeekFlyerEnabled } = useFeatureToggle(Config.FEATURE_TOGGLES.nextWeekFlyer);
  const [isFlyerModalOpen, setIsFlyerModalOpen] = useState(false);
  const { storeFlyerData } = useStoreFlyerData(isNextWeekFlyerEnabled ? storeId : undefined);

  useEffect(() => {
    if (shouldOpenFlyer && storeId && isNextWeekFlyerEnabled && storeFlyerData) {
      setIsFlyerModalOpen(true);
    }
  }, [shouldOpenFlyer, storeId, storeFlyerData, isNextWeekFlyerEnabled]);

  const handleTabClick = useCallback(
    (key: string) => {
      // activeTab follows the URL, which only changes once a navigation finishes. While one is pending, a click on the
      // current tab must still navigate, or it is dropped and the pending navigation lands on the other tab.
      if (key !== activeTab || isNavigating) {
        startNavigation(() => router.push(`${paths.OFFERS}/${key}`));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, isNavigating]
  );

  const getStoreAndSetLocalState = async (id: string) => {
    const store = await getStoreInformation(id);
    if (!store?.external) {
      setSelectedOfflineStore(store);
    }
  };

  useStoreAndLocaleState(getStoreAndSetLocalState);

  const searchResultClickHandler = async (id: string) => {
    try {
      await getStoreAndSetLocalState(id);
      pushGTMEvent({ event: 'Track', category: 'offers_page_store', action: 'store_confirmed', label: id, value: 0 });
      trackOfflineStoreSet('user_selection', id);
      setPromotionStoreId(id);
      setIsSlideInOpen(false);
      setSnack({ text: t('snackbar.storeChosen'), icon: 'valid' });
    } catch (_error) {
      setSnack({ text: t('snackbar.storeSelectionFailed'), icon: null });
    }
  };

  return (
    <>
      {isSlideInOpen && (
        <SlideInSection
          onClose={() => {
            setIsSlideInOpen(false);
            dispatch(toggleSlideInSection(false));
          }}
          closeOnEscape={true}
          hasCloseIcon
        >
          <StyledStoreSearchTitle variant="h3">{t('chooseStoreAction')}</StyledStoreSearchTitle>
          <StoreSearch onResultClick={searchResultClickHandler} onCloseHandler={() => setIsSlideInOpen(false)} />
        </SlideInSection>
      )}
      <StyledPromotionTabsWrapper id="promotionTabs">
        <DeliveryBox
          aria-label={isStoreTab ? t('a11y.storeTabActive') : t('a11y.storeTabInactive')}
          active={isStoreTab}
          onClick={() => handleTabClick(Config.ROUTES.promotionpage.store)}
          icon={<Icon size={24} svg={Store} />}
        >
          <>
            <Text type="subhead">{t('store')}</Text>
            {isStoreTab && (
              <>
                <Text type="body">{storeId ? name : t('chooseStoreInfo')}</Text>

                <StyledOfflineTextWrapper type="body">
                  <StyledButton variant="link" onClick={() => setIsSlideInOpen(true)}>
                    {storeId ? t('changeStore') : t('chooseStoreAction')}
                  </StyledButton>
                  {storeId && flyerURL && (
                    <>
                      {' | '}
                      {isNextWeekFlyerEnabled && storeFlyerData ? (
                        <StyledButton variant="link" onClick={() => setIsFlyerModalOpen(true)}>
                          {t('storeFlyer')}
                        </StyledButton>
                      ) : (
                        <Link href={flyerURL} target="_blank" rel="noreferrer">
                          {t('storeFlyer')}
                        </Link>
                      )}
                    </>
                  )}
                </StyledOfflineTextWrapper>
              </>
            )}
          </>
        </DeliveryBox>

        <DeliveryBox
          icon={<Icon size={24} svg={Cart} />}
          onClick={() => handleTabClick(Config.ROUTES.promotionpage.ecommerce)}
          active={isEcommerceTab}
          aria-label={isEcommerceTab ? t('a11y.ecommerceTabActive') : t('a11y.ecommerceTabInactive')}
        >
          <>
            <Text type="subhead">{t('ecommerce')}</Text>
            {isEcommerceTab && (
              <>
                {ecommerceSelected && showDeliveryInformation ? (
                  <Text type="body" data-testid="addressChanger-line1">
                    {`${deliveryCopy} `}
                    <Text type="body" element="span">
                      {deliveryMode === Config.PICKUP_IN_STORE ? (
                        store.name
                      ) : (
                        <span data-hj-suppress="true">{cart?.deliveryAddress?.formattedAddress}</span>
                      )}
                    </Text>
                  </Text>
                ) : (
                  <Text type="body">
                    {t('pickUpFrom')} {store?.name || ''}
                  </Text>
                )}
                <StyledButton
                  variant="link"
                  onClick={() => dispatch(openDeliveryPicker({ trackingSource: 'from_ecommerce_promotion_card' }))}
                >
                  {ecommerceSelected && showDeliveryInformation ? t('changeStore') : t('chooseDeliveryAction')}
                </StyledButton>
              </>
            )}
          </>
        </DeliveryBox>
      </StyledPromotionTabsWrapper>
      {isFlyerModalOpen && storeFlyerData && storeId && (
        <StoreFlyerModal data={storeFlyerData} onClose={() => setIsFlyerModalOpen(false)} />
      )}
    </>
  );
};

export default PromotionTabs;
